NavigationItemsLoaderService.$inject = [
	'mx.SolutionBuilderAgent.Http',
	'mx.shell.RoutingService',
	'mx.shell.UserProfileService',
	'mx.internationalization',
	'mx.shell.AppsService',
	'mx.SolutionBuilderAgent.PersonalizationService',
	'$q',
	'mx.shell.EventService',
];

export function NavigationItemsLoaderService(http, routingService, userProfileService, i18n, appsService, personalizationService, $q, eventService) {
	let _navCachePromises = null;
	let _navReqPromise = null;
	let currentApp;
	let navigationOrderingPersonalization = {};
	let userProfile;

	return {
		load,
		loadNavigationItem,
		setNavigationIdForStructure,
		getNavigationItemsOfWidget,
	};

	function setNavigationIdForStructure(baseId, data, idProperty) {
		setId(baseId, data);

		function setId(currentNavId, children) {
			children.forEach((item) => {
				item.navigationId = `${item[idProperty]}_${currentNavId}`;
				if (item.Children && item.Children.length) {
					setId(item.navigationId, item.Children);
				}
			});
		}
	}

	/**
	 * Recursivly search for all navigation items related to specific DataSetWidget
	 *
	 * @param items
	 * @param dataSetWidgetId
	 * @returns {Array}
	 * @private
	 */
	function _getNavigationItemsOfDataSetWidget(items, dataSetWidgetId) {
		let result = [];
		items.forEach((navItem) => {
			if (navItem.contentWidgetId === dataSetWidgetId) {
				result.push(navItem);
			}
			if (Array.isArray(navItem.children)) {
				result = result.concat(_getNavigationItemsOfDataSetWidget(navItem.children, dataSetWidgetId));
			}
		});

		return result;
	}

	/**
	 * Function which returns all loaded navigation items which are related to specific DataSetWidget
	 *
	 * @param dataSetWidgetId
	 * @returns {Promise<Array>}
	 */
	function getNavigationItemsOfWidget(dataSetWidgetId) {
		return load().then((navItems) => _getNavigationItemsOfDataSetWidget(navItems, dataSetWidgetId));
	}

	function mapItems(data, parent, isConfigurable) {
		return (data || [])
			.map((item) => {
				// If Nav-item has just one sub-item and this is "Dynamic structure"
				// (doesn't have own sub-items but they can be load dynamically)
				// Like this:
				// "Knowledge Base"  >  "By Type"  >  "Technical Note", "Top Support", ....
				// Then it makes sense to merge into one Nav-Item
				// To get structure like this:
				// "Knowledge Base"  > "Technical Note", "Top Support", ....
				if (
					item.Children &&
					item.Children.length === 1 &&
					item.Children[0].StructureId &&
					item.Children[0].StructureImplementationType &&
					item.Filters &&
					item.Filters.length === 0
				) {
					//TODO: because of this "map" names for Service Catalog => Service | Bundle | Set | Group are similar. Should be fixed
					item.StructureId = item.Children[0].StructureId;
					item.NodeId = item.Children[0].NodeId;
					item.StructureImplementationType = item.Children[0].StructureImplementationType;
					item.RelativePath = item.Children[0].RelativePath;
					item.IsDynamicStructureOnly = true;
					delete item.Children;
					delete item.Filters;
				}

				return mapNavigationItem(item, parent, isConfigurable);
			})
			.filter((x) => x);
	}

	function mapNavigationItem(item, parent, isConfigurable) {
		const url = item.Url;
		let navigationItem = null;

		switch (item.ImplementationType) {
			case mx.SolutionBuilderAgent.NavigationItemImplementation.SearchPage:
				if (item.Widget) {
					if (item.StructureId) {
						navigationItem = new mx.shell.NavigationItem(
							item.Title || item.Name,
							// Navigation Item's `name` should be unique
							//TODO: for Service Catalog => Service | Bundle | Set | Group the names are similar. Should be fixed
							`${item.StructureId}_${item.NodeId}`,
							item.Name,
							routingService.searchPageRoute
						);

						item.Id = item.Id || `${item.NodeId}_${parent.id}`;
						if (parent) {
							item.DataQuery = item.DataQuery || parent.dataQuery;
						}
						navigationItem.parameters = {
							navigationId: item.Id,
							filterId: item.NodeId === item.StructureId ? '' : item.NodeId,
							filterType: 'structure',
							appString: currentApp.name,
						};
						navigationItem.contentWidgetId = item.Widget;

						if (item.NodeId && !item.Children) {
							navigationItem.structureConfig = item;
							navigationItem.childrenDynamicLoader = async () => {
								navigationItem._childrenLoaderPromise = navigationItem._childrenLoaderPromise || childrenDynamicLoader(navigationItem);
								return await navigationItem._childrenLoaderPromise;
							};
						}
					} else if (item.DataQuery) {
						navigationItem = new mx.shell.NavigationItem(item.Title, item.Name, item.Name, routingService.searchPageRoute);

						navigationItem.defaultFilter = item.DefaultFilter;
						navigationItem.parameters = {
							navigationId: item.Id,
							filterId: '',
							filterType: 'nofilter',
							appString: currentApp.name,
						};
						navigationItem.contentWidgetId = item.Widget;
					}
				}
				break;
			case mx.SolutionBuilderAgent.NavigationItemImplementation.CustomController:
				navigationItem = new mx.shell.NavigationItem(
					item.Title,
					item.Name,
					item.Name,
					routingService.createRoutingItem({
						name: `${item.Name}_${mx.components.Utils.guid()}`,
						viewUrl: item.ControllerUrl,
						path: item.Url,
						title: null,
						controller: item.Controller,
					})
				);
				break;
			case mx.SolutionBuilderAgent.NavigationItemImplementation.ExternalUrl:
				navigationItem = new mx.shell.NavigationItem(item.Title, item.Name, item.Name, url);
				navigationItem.openInNewWindow = item.OpenInNewWindow;
				break;
			case mx.SolutionBuilderAgent.NavigationItemImplementation.LandingPage:
				if (item.Widget) {
					navigationItem = new mx.shell.NavigationItem(item.Title, item.Name, item.Name, routingService.landingPageRoute);
					navigationItem.contentWidgetId = item.Widget;
					navigationItem.parameters = {
						navigationId: item.Id,
						appString: currentApp.name,
					};
				}
				break;
			default:
				navigationItem = new mx.shell.NavigationItem(item.Title, item.Name, item.Name);
				break;
		}

		if (navigationItem === null) {
			console.error('Something is wrong with', item);
			return null;
		}

		navigationItem.id = item.Id;
		navigationItem.icon = item.Icon;
		navigationItem.dataQuery = item.DataQuery;
		navigationItem.disabled = item.Disabled;
		navigationItem.application = item.Application;
		navigationItem.availableInSearch =
			item.DataQuery &&
			item.AvailableInSearch !== false &&
			(!item.StructureImplementationType || item.IsDynamicStructureOnly === true) &&
			(navigationItem.parameters?.filterType !== 'structure' || !navigationItem.parameters?.filterId);
		navigationItem.hideLeftSideNavigation = Boolean(item.HideLeftSideNavigation);
		navigationItem.configurable = typeof isConfigurable === 'undefined' ? true : isConfigurable;
		navigationItem.children = mapItems(item.Children, navigationItem);

		if (!parent) {
			navigationItem.icon = navigationItem.icon || 'launch';
		} else if (!navigationItem.parent) {
			navigationItem.parent = parent;
		}

		if (item.Filters && item.Filters.length > 0) {
			let quickFilterFlags = 0;
			const quickFilters = ['', '', ''];
			item.Filters.forEach(({ QuickFilter, Id }) => {
				if (QuickFilter) {
					quickFilterFlags = quickFilterFlags + QuickFilter;
					quickFilters[QuickFilter - 1] = Id;
				}
			});

			if (quickFilterFlags === 6) {
				navigationItem.quickFilters = quickFilters;
			}

			navigationItem.children = navigationItem.children.concat(_buildFiltersTree(item));
		}

		navigationItem.configurable = _isConfigurableItem(item, isConfigurable);

		return navigationItem;
	}

	async function mergeNavItems(originNav, loadedNav) {
		await reloadAlreadyLoadedForMerge(originNav, loadedNav);
		originNav = await _mergeNavItemsAddingAndEditing(originNav, loadedNav);
		originNav = _mergeNavItemsDeleting(originNav, loadedNav);
		return originNav;
	}

	async function reloadAlreadyLoadedForMerge(originNav = [], loadedNav = []) {
		for (const loadedNavItem of loadedNav) {
			const originNavItem = originNav.find((originNavItem) => originNavItem.id === loadedNavItem.id);
			if (originNavItem) {
				if (originNavItem.dynamicChildrenLoaded && loadedNavItem.childrenDynamicLoader) {
					loadedNavItem.children = await loadDynamicChildren(loadedNavItem);
				}
				await reloadAlreadyLoadedForMerge(originNavItem.children, loadedNavItem.children);
			}
		}
	}

	async function _mergeNavItemsAddingAndEditing(originNav = [], loadedNav = [], originParent) {
		for (const loadedNavItem of loadedNav) {
			const originNavItem = originNav.find((originNavItem) => originNavItem.id === loadedNavItem.id);
			if (originNavItem) {
				originNavItem.title = loadedNavItem.title;
				_mergeNavItemsAddingAndEditing(originNavItem.children, loadedNavItem.children, originNavItem);
			} else {
				originNav.push(loadedNavItem);
				if (originParent?.configurationModeOn) {
					loadedNavItem.configurationModeOn = true;
				}
			}
		}
		return originNav;
	}

	function _mergeNavItemsDeleting(originNav = [], loadedNav = []) {
		if (originNav.length > loadedNav.length) {
			for (let i = originNav.length - 1; i > -1; i--) {
				if (!loadedNav.some((loadedItem) => loadedItem.id === originNav[i].id)) {
					originNav.splice(i, 1);
				}
			}
		}
		originNav.forEach((originNavItem) => {
			const loadedNavItem = loadedNav.find((loadedNavItem) => {
				return loadedNavItem.id === originNavItem.id;
			});

			if (loadedNavItem) {
				_mergeNavItemsDeleting(originNavItem.children, loadedNavItem.children);
			}
		});
		return originNav;
	}

	async function childrenDynamicLoader(navigationItem) {
		if (Array.isArray(navigationItem.children) && navigationItem.dynamicChildrenLoaded) {
			return navigationItem.children;
		}

		const newChildren = await loadDynamicChildren(navigationItem);

		navigationItem.children = await mergeNavItems(navigationItem.children, newChildren);
		_visitChildrenTree(navigationItem.children, navigationItem.level);

		eventService.dispatchEvent(mx.shell.EventCodes.NavigationChildrenLoaded, {
			parent: navigationItem.structureConfig,
			children: navigationItem.children,
		});
		navigationItem.dynamicChildrenLoaded = true;
		return navigationItem.children;
	}

	async function loadDynamicChildren(navigationItem) {
		let children = [];

		switch (navigationItem.structureConfig.StructureImplementationType) {
			case mx.SolutionBuilderAgent.StructureNavigationItemImplementation.DataDefinition:
				children = await dataDefinitionChildrenLoader(navigationItem.structureConfig);
				break;
			case mx.SolutionBuilderAgent.StructureNavigationItemImplementation.ServiceOperation:
				children = await serviceChildrenLoader(navigationItem.structureConfig);
				break;
		}

		return mapItems(children, navigationItem, false);
	}

	async function dataDefinitionChildrenLoader(item) {
		return await http.get(`api/Navigation/Children/${item.Widget}/${item.NodeId}`);
	}

	async function serviceChildrenLoader(item) {
		const children = await http.get(
			buildPath(item.RelativePath, {
				widgetId: item.Widget,
				nodeId: item.NodeId,
			})
		);
		setStructureChildrenProperties(children, item);
		return children;
	}

	function _visitChildrenTree(children, level) {
		children.forEach((child) => {
			child.level = level + 1;

			if (child.children && child.children.length) {
				_visitChildrenTree(child.children, child.level);
			}
		});
	}

	// Navigation Item is configurable if it or its child has filter(s)
	// (according to code-logic - not just that case)
	function _isConfigurableItem({ Filters, Children }, forcedValue) {
		if (typeof forcedValue !== 'undefined') {
			return forcedValue;
		}

		let i;
		let res = false;

		if (Filters && Filters.length > 0) {
			res = true;
		} else if (Children) {
			for (i = 0; i < Children.length; i++) {
				if (_isConfigurableItem(Children[i])) {
					res = true;
					break;
				}
			}
		}
		return res;
	}

	function applyPersonalizedPosition(node, parent, navigationItem) {
		const filtersOrdering = navigationOrderingPersonalization[`${navigationItem.Id}_${parent.Id}`];
		if (filtersOrdering) {
			node.Position = typeof filtersOrdering[node.Id] === 'number' ? filtersOrdering[node.Id] : node.Position;
		}
	}

	function sortByPosition({ Position: positionA = 0 }, { Position: positionB = 0 }) {
		return positionA - positionB;
	}

	function sortFiltersTree(tree) {
		tree.sort(sortByPosition);
		tree.forEach((node) => {
			if (Array.isArray(node.Children)) {
				sortFiltersTree(node.Children);
			}
		});
	}

	function _buildFiltersTree(item) {
		const tree = [];
		item.Filters.forEach((node) => {
			if (node.Parent) {
				const parentFilter = item.Filters.find(({ Id }) => Id === node.Parent);
				if (parentFilter) {
					applyPersonalizedPosition(node, parentFilter, item);

					if (Array.isArray(parentFilter.Children)) {
						parentFilter.Children.push(node);
					} else {
						parentFilter.Children = [node];
					}
				}
			} else {
				applyPersonalizedPosition(node, item, item);
				tree.push(node);
			}
		});

		sortFiltersTree(tree);

		return tree.map((node) => _createFilterNavigationItem(node, item, userProfile));
	}

	function _createFilterNavigationItem(filter, navigationItem, userProfile) {
		const filterNavigation = new mx.shell.NavigationItem(filter.Title, filter.Id, filter.TitleNeutral, routingService.searchPageRoute);

		filterNavigation.parameters = {
			navigationId: filter.NavigationItem,
			filterId: filter.Id,
			filterType: 'filter',
			appString: currentApp.name,
		};

		filterNavigation.contentWidgetId = navigationItem.Widget;
		filterNavigation.__fromFilter = true;
		filterNavigation.__fromContext = navigationItem.Name;
		filterNavigation.name += filterNavigation.__fromContext;
		filterNavigation.parent = filter.Parent;
		filterNavigation.id = filter.Id;
		filterNavigation.availableInSearch = false;
		filterNavigation.originFilterReference = filter;

		filterNavigation.draggable = true;

		if (Array.isArray(filter.Children)) {
			filterNavigation.children = filter.Children.map((node) => _createFilterNavigationItem(node, navigationItem, userProfile));
		}

		return filterNavigation;
	}

	/**
	 * @ngdoc method
	 * @async
	 * @name mx.shell.NavigationItemsLoaderService#load
	 *
	 * @description
	 * Loads navigation items.
	 *
	 * @param {boolean} reload - force reload
	 * @param {NavigationItem[]} existedNavItems - existed navigation items to merge with new loaded items; used to keep locally changed titles
	 *
	 * @returns {promise} Resolved as an array of {@ref mx.shell.NavigationItem navigation items}. See {@ref mx.shell.NavigationItem navigation item} for details
	 */
	function load(reload, existedNavItems) {
		reload = reload === true;
		if (!_navCachePromises || reload) {
			_navCachePromises = appsService.getCurrentApplication().then((app) => {
				currentApp = app;
				if (!app || !app.value) {
					return [];
				}
				return (
					_navReqPromise ||
					(_navReqPromise = $q
						.all([http.get(`api/Navigation?app=${app.value}`), userProfileService.get(), personalizationService.getAllNavigationFiltersOrdering()])
						.then((data) => {
							userProfile = data[1];
							navigationOrderingPersonalization = data[2];
							_navReqPromise = null;
							return mapItems(data[0], null);
						})).then((loadedNavData) => {
						return existedNavItems ? mergeNavItems(existedNavItems, loadedNavData) : loadedNavData;
					})
				);
			});
		}

		return _navCachePromises;
	}

	function buildPath(relativePath, params) {
		angular.forEach(params, (value, key) => {
			const regex = new RegExp(`{${key}:*\\w*\\?*}`, 'gi');

			relativePath = relativePath.replace(regex, value);
		});

		return relativePath;
	}

	function setStructureChildrenProperties(children, structure) {
		(children || []).forEach((child) => {
			child.Parent = structure.Id;
			child.Widget = structure.Widget;
			child.ImplementationType = structure.ImplementationType;
			child.StructureId = structure.StructureId;

			if (child.Children) {
				setStructureChildrenProperties(child.Children, structure);
			}
		});
	}

	function loadNavigationItem(navigationItemId) {
		return http.get(`api/Navigation/Available/${navigationItemId}`);
	}
}
